import { useCallback, useState } from 'react';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import styled from 'styled-components';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import { CSSTransition } from 'react-transition-group';
import { CRM_FULL_WIDTH } from '../../crm-sidebar/constants';
import ThreadViewProvider from '../../thread-view/components/ThreadViewProvider';
import InboxContextualSidebar from './InboxContextualSidebar';
import InstallCodePaneWrapperContainer from '../../verify-install/containers/InstallCodePaneWrapperContainer';
import { useLocalStorage } from '../../local-storage/hooks/useLocalStorage';
import { ConversationsTaxonomyPanel } from 'zorse-crm-components-lib';
import { useTaxonomyFlow } from '../../zorse/hooks/useTaxonomyFlow';
import InboxViewMemberDetailsHeader from '../../detail-header/components/InboxViewMemberDetailsHeader';
import { useConversationHandoffErrorAlert } from '../hooks/useConversationHandoffErrorAlert';
import InboxThreadView from './InboxThreadView';
import { TOOLBAR_HEIGHT } from '../../constants/Dimensions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_SCREENWIDTH = 1260;
const MIN_MAIN_CONTEXT_WIDTH = 400;
const MIN_MAIN_CONTEXT_WIDTH_WITH_SIDEBAR = MIN_MAIN_CONTEXT_WIDTH + CRM_FULL_WIDTH;
const Toolbar = styled(UIBox).withConfig({
  displayName: "InboxThreadOverview__Toolbar",
  componentId: "sc-450ge-0"
})(["height:", "px;border-bottom:1px solid ", ";"], TOOLBAR_HEIGHT, GREAT_WHITE);
const ThreadViewSection = styled(UIBox).withConfig({
  displayName: "InboxThreadOverview__ThreadViewSection",
  componentId: "sc-450ge-1"
})(["height:calc(100% - ", "px);width:100%;border-right:1px solid ", ";min-width:", "px;"], TOOLBAR_HEIGHT, GREAT_WHITE, MIN_MAIN_CONTEXT_WIDTH);
const RightSidebar = styled(UIBox).withConfig({
  displayName: "InboxThreadOverview__RightSidebar",
  componentId: "sc-450ge-2"
})(["width:", "px;"], CRM_FULL_WIDTH);
const ContextualSidebarWrapper = styled.div.withConfig({
  displayName: "InboxThreadOverview__ContextualSidebarWrapper",
  componentId: "sc-450ge-3"
})(["position:absolute;height:calc(100% - ", "px);width:", "px;"], TOOLBAR_HEIGHT, CRM_FULL_WIDTH);
const ThreadOverviewWrapper = styled(UIFlex).withConfig({
  displayName: "InboxThreadOverview__ThreadOverviewWrapper",
  componentId: "sc-450ge-4"
})([".right-sidebar-enter{width:0%;opacity:0;}.right-sidebar-enter-active{opacity:1;width:", "px;transition:all 200ms cubic-bezier(0.42,0,0.58,1);}.right-sidebar-exit{width:", ";}.right-sidebar-exit-active{width:0%;opacity:0;transition:all 200ms cubic-bezier(0.42,0,0.58,1);}"], CRM_FULL_WIDTH, CRM_FULL_WIDTH);
const MainContext = styled(UIBox).withConfig({
  displayName: "InboxThreadOverview__MainContext",
  componentId: "sc-450ge-5"
})(["min-width:", "px;height:100%;"], MIN_MAIN_CONTEXT_WIDTH);
const CONTEXTUAL_SIDEBAR_STATE = 'CONTEXTUAL_SIDEBAR_STATE';
const OPEN = 'OPEN';
const CLOSED = 'CLOSED';
const InboxThreadOverview = ({
  focusedThreadId
}) => {
  const initialToggleState = window.innerWidth < MIN_SCREENWIDTH ? CLOSED : OPEN;
  const [item, setItem] = useLocalStorage({
    key: CONTEXTUAL_SIDEBAR_STATE,
    initialValue: initialToggleState
  });
  const isOpen = item === OPEN;
  const toggleSidebar = useCallback(() => setItem(item === OPEN ? CLOSED : OPEN), [item, setItem]);
  const MainContextMinWidth = isOpen ? `${MIN_MAIN_CONTEXT_WIDTH_WITH_SIDEBAR}px` : `${MIN_MAIN_CONTEXT_WIDTH}px`;
  const [isTaxonomyPanelOpen, setIsTaxonomyPanelOpen] = useState(false);
  const {
    usesTaxonomyFlow
  } = useTaxonomyFlow(focusedThreadId);
  const openZorseTaxonomyPanel = useCallback(() => {
    if (usesTaxonomyFlow) {
      setIsTaxonomyPanelOpen(true);
    }
  }, [usesTaxonomyFlow]);
  const closeZorseTaxonomyPanel = () => {
    if (usesTaxonomyFlow) {
      setIsTaxonomyPanelOpen(false);
    }
  };
  const renderZorseTaxonomyPanel = () => {
    return usesTaxonomyFlow && isTaxonomyPanelOpen ? /*#__PURE__*/_jsx(ConversationsTaxonomyPanel, {
      "data-test-id": "conversations-taxonomy-panel",
      threadId: Number(focusedThreadId),
      onClose: closeZorseTaxonomyPanel
    }) : null;
  };
  useConversationHandoffErrorAlert(focusedThreadId);
  return /*#__PURE__*/_jsxs(ThreadViewProvider, {
    threadId: focusedThreadId,
    children: [/*#__PURE__*/_jsxs(MainContext, {
      grow: 1,
      shrink: 0,
      basis: MainContextMinWidth,
      children: [/*#__PURE__*/_jsx(Toolbar, {
        className: "flex-column",
        children: /*#__PURE__*/_jsx(InboxViewMemberDetailsHeader, {
          threadId: focusedThreadId,
          toggleSidebar: toggleSidebar,
          openZorseTaxonomyPanel: openZorseTaxonomyPanel
        })
      }), /*#__PURE__*/_jsxs(ThreadOverviewWrapper, {
        className: "height-100",
        align: "stretch",
        children: [/*#__PURE__*/_jsx(ThreadViewSection, {
          "aria-live": "polite",
          children: /*#__PURE__*/_jsx(InstallCodePaneWrapperContainer, {
            focusedThreadId: focusedThreadId,
            children: /*#__PURE__*/_jsx(InboxThreadView, {
              threadId: focusedThreadId,
              openZorseTaxonomyPanel: openZorseTaxonomyPanel,
              isContextualSidebarOpen: isOpen
            })
          })
        }), /*#__PURE__*/_jsx(CSSTransition, {
          in: isOpen,
          timeout: 100,
          classNames: "right-sidebar",
          mountOnEnter: true,
          unmountOnExit: true,
          children: /*#__PURE__*/_jsx(RightSidebar, {
            grow: 0,
            shrink: 0,
            basis: "auto",
            children: /*#__PURE__*/_jsx(ContextualSidebarWrapper, {
              children: /*#__PURE__*/_jsx(InboxContextualSidebar, {
                focusedThreadId: focusedThreadId
              })
            })
          })
        })]
      })]
    }), renderZorseTaxonomyPanel()]
  });
};
InboxThreadOverview.displayName = 'InboxThreadOverview';
export default InboxThreadOverview;